import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

/**
 * The blog's tag index — the data layer behind the topic chips.
 *
 * `sections/sl-blog-tag-index.liquid` renders one `paginate ... by 250` pass over the CURRENT
 * `blog.articles` (already narrowed by the `/tagged/` URL) and emits the tag handles of every
 * article as JSON. This component parses that, applies the taxonomy config, and answers the two
 * questions the chip UI asks: which topics exist, and how many articles each would leave.
 *
 * Why it exists at all: Shopify hands out `blog.tags` (which topics are reachable) and
 * `paginate.items` (the filtered total) for free, but there is no native per-tag count. That is
 * the one number the design needs — `Diagnosis & Treatment (61)` — so it is computed here.
 *
 * It is rendered ONCE, on page load, and never re-fetched: filtering only ever narrows within the
 * current category, so every later combination is a subset of what this already holds. That is
 * what lets the chips and their counts update with no network at all.
 *
 * COUNTS USE LITERAL HANDLES, NOT CANONICALISED ONES. A chip's number has to match what clicking
 * it actually returns, and clicking navigates to `/tagged/<canonical>` which the server filters on
 * the literal tag. So grouping only ever HIDES the variant chip; it never folds the variant's
 * articles into the canonical's count. Articles carrying a variant but not its canonical fall out
 * of the topic filter entirely until they are re-tagged — which is exactly what the audit panel
 * below exists to surface.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data - The JSON payload.
 * @property {HTMLElement} [audit] - Debug panel host, present only when the merchant enables it.
 *
 * @typedef {object} Payload
 * @property {number} items
 * @property {number} page
 * @property {number} pages
 * @property {Record<string, string>} labels
 * @property {string[]} articles
 * @property {string[]} [handles]
 * @property {string[]} [titles]
 */

/** The event other components wait on before reading the index. */
export const BLOG_INDEX_READY = 'sl-blog-index:ready';

/** The live index, once it has parsed its payload. @type {SlBlogIndexComponent | null} */
let readyIndex = null;

/** @type {Set<(index: SlBlogIndexComponent) => void>} */
const subscribers = new Set();

/**
 * Subscribe to the tag index.
 *
 * This is deliberately not a ref: the index is its own section, so a consumer cannot reach it
 * through `this.refs`, and the two can render either side of each other in the template. The
 * callback runs immediately when the index is already up, and again on every later announcement —
 * which is how a consumer picks up the idle backfill of the pages beyond the first without polling.
 *
 * @param {(index: SlBlogIndexComponent) => void} callback
 * @returns {() => void} Unsubscribe.
 */
export function subscribeToBlogIndex(callback) {
  subscribers.add(callback);
  if (readyIndex) callback(readyIndex);
  return () => subscribers.delete(callback);
}

/** @extends {Component<Refs>} */
export class SlBlogIndexComponent extends Component {
  /** @type {string[]} */
  requiredRefs = ['data'];

  /** Tag handles per article, exactly as authored. @type {Set<string>[]} */
  #articles = [];

  /** handle -> display label, first spelling wins. @type {Map<string, string>} */
  #labels = new Map();

  /** Debug only — parallel to `#articles`. @type {{ handle: string, title: string }[]} */
  #meta = [];

  /** Explicit denylist plus every group variant. @type {Set<string>} */
  #denied = new Set();

  /** canonical -> variants. @type {Map<string, string[]>} */
  #groups = new Map();

  #minArticles = 1;
  #total = 0;
  #pages = 1;

  /**
   * Which server pages of the index are in hand.
   *
   * A Set rather than a high-water mark, because the first payload is not necessarily page 1:
   * `?page=2` in the URL makes `{% paginate %}` render page 2 here too, and a counter would
   * then call a two-page index complete while holding only its 16-article tail — every chip
   * would drop under the minimum and the row would vanish. `paginated-list.js` pushes `?page=`
   * as the shopper scrolls, so that URL is reachable, and stays reachable through shared links.
   * @type {Set<number>}
   */
  #loadedPages = new Set();

  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    if (readyIndex === this) readyIndex = null;
  }

  // ----------------------------------------------------------------- public

  /** The server's count for the current `/tagged/` URL — `paginate.items`, not a tally. */
  get total() {
    return this.#total;
  }

  /** False while the index still misses any page, whichever page the request happened to land on. */
  get complete() {
    return this.#loadedPages.size >= this.#pages;
  }

  /**
   * Topic handles worth showing, most-populated first, ties broken alphabetically so the order is
   * stable between renders.
   * @returns {string[]}
   */
  topics() {
    return [...this.counts(new Set())]
      .filter(([, n]) => n >= this.#minArticles)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([handle]) => handle);
  }

  /**
   * How many articles carry each tag, among those matching `selection`.
   * @param {Set<string>} selection - Handles that must ALL be present (AND), matching the `+` semantics of the URL.
   * @returns {Map<string, number>}
   */
  counts(selection) {
    /** @type {Map<string, number>} */
    const counts = new Map();

    for (const tags of this.#articles) {
      if (!this.matches(tags, selection)) continue;
      for (const tag of tags) {
        if (this.#denied.has(tag)) continue;
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }

    return counts;
  }

  /**
   * How many articles the given selection leaves.
   * @param {Set<string>} selection
   * @returns {number}
   */
  matchCount(selection) {
    let n = 0;
    for (const tags of this.#articles) if (this.matches(tags, selection)) n += 1;
    return n;
  }

  /**
   * @param {Set<string>} tags
   * @param {Set<string>} selection
   * @returns {boolean}
   */
  matches(tags, selection) {
    for (const handle of selection) if (!tags.has(handle)) return false;
    return true;
  }

  /**
   * @param {string} handle
   * @returns {string} The tag as the merchant spelled it, falling back to the handle.
   */
  label(handle) {
    return this.#labels.get(handle) ?? handle;
  }

  /** @param {string} handle @returns {boolean} */
  isTopic(handle) {
    return !this.#denied.has(handle);
  }

  // ------------------------------------------------------------------ setup

  #init() {
    this.#abort.abort();
    this.#abort = new AbortController();

    this.#readConfig();

    const payload = this.#parse(this.refs.data.textContent);
    if (!payload) return;

    this.#articles = [];
    this.#labels = new Map();
    this.#meta = [];
    this.#total = payload.items;
    this.#pages = payload.pages;
    this.#loadedPages = new Set();

    this.#absorb(payload);

    // Above 250 articles the pass is short. The remainder is not needed to draw the chips, so it
    // is pulled once the main thread is free rather than held in front of first paint.
    if (!this.complete) requestIdleCallback(() => this.#fetchRest());

    this.#renderAudit();
    this.#announce();
  }

  /** Publish to the event listeners and to the module-level subscribers. */
  #announce() {
    readyIndex = this;
    this.dispatchEvent(new CustomEvent(BLOG_INDEX_READY, { bubbles: true, detail: { index: this } }));
    for (const callback of subscribers) callback(this);
  }

  #readConfig() {
    this.#denied = new Set(
      (this.dataset.denylist ?? '')
        .split(',')
        .map((h) => h.trim())
        .filter(Boolean)
    );

    // `canonical:variant,variant;canonical:variant` — Liquid handleizes both sides, and a handle
    // can never contain `:` or `;`, so the separators are safe.
    this.#groups = new Map();
    for (const group of (this.dataset.groups ?? '').split(';')) {
      const [canonical, rest] = group.split(':');
      if (!canonical || !rest) continue;
      const variants = rest.split(',').filter(Boolean);
      if (!variants.length) continue;
      this.#groups.set(canonical, variants);
      // A variant is hidden the same way a denylisted tag is — one mechanism, not two.
      for (const variant of variants) this.#denied.add(variant);
    }

    const min = Number(this.dataset.minArticles);
    this.#minArticles = Number.isFinite(min) && min > 0 ? min : 1;
  }

  /**
   * @param {string | null} text
   * @returns {Payload | null}
   */
  #parse(text) {
    try {
      return JSON.parse(text ?? '');
    } catch {
      console.error('sl-blog-index: unparseable payload');
      return null;
    }
  }

  /** @param {Payload} payload */
  #absorb(payload) {
    for (const [handle, label] of Object.entries(payload.labels ?? {})) {
      if (!this.#labels.has(handle)) this.#labels.set(handle, label);
    }

    payload.articles.forEach((tags, i) => {
      this.#articles.push(new Set(tags.split(' ').filter(Boolean)));
      if (payload.handles) {
        this.#meta.push({ handle: payload.handles[i] ?? '', title: payload.titles?.[i] ?? '' });
      }
    });

    this.#loadedPages.add(payload.page);
  }

  /** Pull whichever pages of the index are still missing, through the Section Rendering API. */
  async #fetchRest() {
    for (let page = 1; page <= this.#pages; page += 1) {
      if (this.#loadedPages.has(page)) continue;

      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('page', String(page));

      let payload = null;
      try {
        const response = await fetch(url, { signal: this.#abort.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        payload = this.#parse(doc.querySelector('[ref="data"]')?.textContent ?? null);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('sl-blog-index: could not complete the index', error);
        return;
      }

      if (!payload) return;
      this.#absorb(payload);
    }

    this.#renderAudit();
    this.#announce();
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('sl-blog-index: the section-id attribute is required');
    return id;
  }

  // ------------------------------------------------------------------ audit

  /**
   * Merchant-facing debug panel. Two tables: every tag with its count, and — the one that matters —
   * the articles each group is losing. An article carrying `cpap-masks` but not `cpap-mask` shows
   * under no chip at all, and the only fix is re-tagging it in Admin. Listing them here means the
   * list maintains itself instead of being re-derived by hand every time.
   */
  #renderAudit() {
    const host = this.refs.audit;
    if (!host) return;

    const counts = this.counts(new Set());
    const rows = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    const shown = rows.filter(([, n]) => n >= this.#minArticles).length;

    const fragment = document.createDocumentFragment();

    fragment.append(
      this.#note(
        `${this.#articles.length} of ${this.#total} articles indexed` +
          `${this.complete ? '' : ' (incomplete — still loading)'} · ` +
          `${rows.length} tags after the denylist · ${shown} pass the ${this.#minArticles}-article floor`
      )
    );

    const chips = document.createElement('div');
    chips.className = 'sl-blog-index__chips';
    for (const [handle, n] of rows) {
      const chip = document.createElement('span');
      chip.className = 'sl-blog-index__chip';
      if (n < this.#minArticles) chip.classList.add('is-below-floor');
      chip.textContent = `${this.label(handle)} (${n})`;
      chips.append(chip);
    }
    for (const handle of [...this.#denied].sort()) {
      const chip = document.createElement('span');
      chip.className = 'sl-blog-index__chip is-denied';
      chip.textContent = this.label(handle);
      chips.append(chip);
    }
    fragment.append(chips);

    for (const [canonical, variants] of this.#groups) {
      for (const variant of variants) {
        const orphans = this.#orphans(variant, canonical);
        if (!orphans.length) continue;

        fragment.append(
          this.#note(
            `${orphans.length} article${orphans.length === 1 ? '' : 's'} tagged ` +
              `"${this.label(variant)}" but not "${this.label(canonical)}" — ` +
              `add "${canonical}" in Admin or they appear under no topic:`,
            'is-warning'
          )
        );

        const list = document.createElement('ul');
        list.className = 'sl-blog-index__orphans';
        for (const { handle, title } of orphans) {
          const item = document.createElement('li');
          item.textContent = title ? `${title} — /blogs/news/${handle}` : `/blogs/news/${handle}`;
          list.append(item);
        }
        fragment.append(list);
      }
    }

    host.replaceChildren(fragment);
  }

  /**
   * @param {string} variant
   * @param {string} canonical
   * @returns {{ handle: string, title: string }[]}
   */
  #orphans(variant, canonical) {
    /** @type {{ handle: string, title: string }[]} */
    const out = [];
    this.#articles.forEach((tags, i) => {
      if (tags.has(variant) && !tags.has(canonical)) {
        out.push(this.#meta[i] ?? { handle: `#${i}`, title: '' });
      }
    });
    return out;
  }

  /**
   * @param {string} text
   * @param {string} [modifier]
   * @returns {HTMLParagraphElement}
   */
  #note(text, modifier) {
    const note = document.createElement('p');
    note.className = 'sl-blog-index__note';
    if (modifier) note.classList.add(modifier);
    note.textContent = text;
    return note;
  }
}

if (!customElements.get('sl-blog-index-component')) {
  customElements.define('sl-blog-index-component', SlBlogIndexComponent);
}
